import apiClient from "src/utils/apiClient";
import { ConversationDetails, ConversationSummary, Turn } from "../types";
import {
  ConversationDetailsDTO,
  ConversationDTO,
  GetConversationsDTO,
  TurnDTO,
} from "./types";

class AdminService {
  private readonly baseURL = "/admin";

  // data mapping
  private mapConversation(dto: ConversationDTO): ConversationSummary {
    return {
      id: dto.conversation_id,
      lastMessageTime: new Date(dto.last_message_time),
      userAlias: dto.user_alias,
      confirmedYes: dto.confirmed_yes,
      confirmedNo: dto.confirmed_no,
      voteUp: dto.vote_up,
      voteDown: dto.vote_down,
      turnCount: dto.turn_count,
      durationInMinutes: dto.duration_in_minutes,
      hasEmptyData: dto.has_empty_data,
      exceptionCount: dto.exception_count,
      favoriteCount: dto.favorite_count,
      saigeQuestionCount: dto.saige_question_count,
      saigeNoAnswerCount: dto.saige_no_answer_count,
    };
  }

  private mapTurn(dto: TurnDTO): Turn {
    if (dto.type === "confirmation") {
      return {
        type: dto.type,
        confirmedYes: dto.confirmed_yes,
        chartId: dto.chart_id,
        voteUp: dto.vote_up,
        downvoteReason: dto.downvote_reason,
        emptyData: dto.empty_data,
        numericalAnswer: dto.numerical_answer,
        showCsv: dto.show_csv || false,
      };
    } else if (dto.type === "text") {
      return {
        type: dto.type,
        userRequest: dto.user_request,
        systemResponse: dto.system_response,
        responseDetails: dto.response_details,
        isFavorite: dto.is_favorite,
        relatedDocuments: dto.related_documents,
        voteUp: dto.vote_up,
        downvoteReason: dto.downvote_reason,
        sourceAttributions: dto.source_attributions,
      };
    } else if (dto.type === "exception")
      return {
        type: dto.type,
        userRequest: dto.user_request,
        systemResponse: dto.system_response,
        exceptionMessage: dto.exception_message,
        isTimeoutRisk: dto.is_timeout_risk,
        processingTimeMs: dto.processing_time_ms,
      };
  }

  // api service methods
  async getConversations(): Promise<ConversationSummary[]> {
    const { data } = await apiClient.get<GetConversationsDTO>(
      `${this.baseURL}/conversations`,
    );
    return data.conversations.map((conversation) =>
      this.mapConversation(conversation),
    );
  }

  async getChartDetails(chartId: string) {
    const { data } = await apiClient.get(`${this.baseURL}/charts/${chartId}`);

    return data.chart_json;
  }

  async getCsvData(chartId: string) {
    const { data } = await apiClient.get(`${this.baseURL}/csv/${chartId}`);
    return {
      csvPreview: data.csv_preview,
      csvData: data.csv_data,
    };
  }

  async getConversationDetails(
    conversationId: string,
  ): Promise<ConversationDetails> {
    const { data } = await apiClient.get<ConversationDetailsDTO>(
      `${this.baseURL}/conversations/${conversationId}`,
    );
    return {
      summary: this.mapConversation(data.summary),
      turns: data.turns.map((turn) => this.mapTurn(turn)),
    };
  }
}

export default new AdminService();
